import { useEffect, useMemo, useRef, useState } from "react";
import type { CellContext, ColumnDef } from "@tanstack/react-table";
import {
  DataTable,
  useIsMobile,
  Button as GetiButton,
  Dialog as GetiDialog,
  DialogContent as GetiDialogContent,
  DialogDescription as GetiDialogDescription,
  DialogFooter as GetiDialogFooter,
  DialogHeader as GetiDialogHeader,
  DialogTitle as GetiDialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type DataTableViewMode,
} from "@geti/identidade-visual-react";
import {
  MdArrowDownward,
  MdArrowUpward,
  MdDriveFileRenameOutline,
  MdKeyboardArrowDown,
  MdMoreHoriz,
  MdOutlineDelete,
  MdOutlineEditNote,
  MdOutlineFileOpen,
  MdOutlinePushPin,
  MdOutlineSubtitlesOff,
  MdSearch,
} from "react-icons/md";
import { toast } from "sonner";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { isLoggedIn } from "../../../Auth/auth";
import { formatFileName, returnHandleExtractUrl } from "../../../Components/Helper";
import { PATH } from "../../../PATH";
import { fetchDeletePublishedFile } from "../../../Services/Slices/deletePublishedFileSlice";
import { fetchFavoritePosts } from "../../../Services/Slices/favoritePostsSlice";
import { fetchListPosts } from "../../../Services/Slices/listPostsSlice";
import { patchFavoritePost } from "../../../Services/Slices/patchFavoritePost";
import { updatePost } from "../../../Services/Slices/postSlice";
import { patchPostDescription } from "../../../Services/Slices/updatePostDescriptionSlice";
import styles from "./HomeDataTable.module.css";
import Tooltips from "../../../Components/Tooltip/Tooltip";

type HomeTableRow = {
  date: string;
  edition: string;
  presigned_url: string;
  fileKey: string;
  status: string;
  title: string;
  origin: string;
  action?: string;
  [key: string]: any;
};

interface HomeDataTableProps {
  title?: string;
  data: HomeTableRow[];
  setPage: (page: number) => void;
  page: number;
  total?: number;
  isEmpty?: boolean;
  loading?: boolean;
  error?: boolean;
  onToggleReverse?: () => void;
  /** Espelha `reverse_posts` na API: alterna ordem crescente/decrescente por publicação. */
  reversePosts?: boolean;
  isSearched?: boolean;
}

const HOME_DATATABLE_COLUMN_VISIBILITY_KEY = "atos-ui-home-posts";

/** Sentinel para o Select GETI quando nenhum ato responsável foi escolhido ainda */
const REVOKE_RESPONSIBLE_NONE = "__revoke_responsible_none__";

const EDITION_MENU_DIALOG_CONTENT_CLASS =
  "box-border w-full gap-0 p-0";
const EDITION_MENU_ACTION_BUTTON_CLASS =
  "h-auto min-h-12 w-full justify-start gap-3 px-4 py-3 text-left font-medium shadow-none";
const EDITION_MENU_ICON_WRAP_CLASS =
  "flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground";
const EDITION_MENU_TEXT_COL_CLASS = "flex min-w-0 flex-col items-start gap-0.5";
const EDITION_MENU_SUBTITLE_CLASS = "text-xs font-normal text-muted-foreground";

const EDITION_MENU_REVOKE_ICON_WRAP_CLASS =
  "flex size-9 shrink-0 items-center justify-center rounded-lg bg-destructive/10 text-destructive";
const EDITION_MENU_REVOKE_SUBTITLE_CLASS = "text-xs font-normal text-destructive/80";

export function HomeDataTable({
  title,
  data,
  setPage,
  page,
  total = 0,
  isEmpty,
  loading,
  error,
  onToggleReverse,
  reversePosts = false,
  isSearched,
}: HomeDataTableProps) {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openSecondDialog, setOpenSecondDialog] = useState(false);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [deleteCountdown, setDeleteCountdown] = useState(5);
  const [revokeCountdown, setRevokeCountdown] = useState(5);
  const [openDialogDescription, setOpenDialogDescription] = useState(false);
  const [fileKeyToRevoke, setFileKeyToRevoke] = useState<string | null>(null);
  const [fileKeyName, setFileKeyName] = useState<string | null>(null);
  const [responsibleRevokeFileName, setResponsibleRevokeFileName] = useState<
    string | undefined
  >(undefined);
  const [description, setDescription] = useState("");
  const [message, setMessage] = useState("");
  const [caracterLimit, setCaracterLimit] = useState(false);
  const [fileName, setFileName] = useState("");
  const [deleteRow, setDeleteRow] = useState<any>();
  const [posts, setPosts] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<DataTableViewMode>("table");
  const isMobile = useIsMobile();

  const favoritePosts = useSelector((state: any) => state.favoritePostsSlice.data);
  const listPosts = useSelector((state: any) => state.listPostsSlice);
  const deletePublishedState = useSelector((state: any) => state.deletePublishedFileSlice);
  const postSliceState = useSelector((state: any) => state.postSlice);
  const deletePendingRef = useRef(false);
  const revokePendingRef = useRef(false);

  useEffect(() => {
    if (isLoggedIn()) {
      dispatch(fetchListPosts("1", true));
      dispatch(fetchFavoritePosts());
    }
  }, [dispatch]);

  useEffect(() => {
    if (favoritePosts?.favorite_posts) {
      setPosts(favoritePosts.favorite_posts);
    }
  }, [favoritePosts]);

  useEffect(() => {
    if (deletePublishedState.loading || !deletePendingRef.current) return;
    deletePendingRef.current = false;
    // Delete errors are toasted by useApiErrorToast in Home.
    if (!deletePublishedState.error) {
      toast.success("Arquivo excluído com sucesso");
    }
  }, [deletePublishedState.loading, deletePublishedState.error]);

  useEffect(() => {
    if (postSliceState.loading || !revokePendingRef.current) return;
    revokePendingRef.current = false;
    if (postSliceState.error) {
      toast.error("Erro ao revogar ato");
    } else {
      toast.success("Ato revogado com sucesso");
    }
  }, [postSliceState.loading, postSliceState.error]);

  useEffect(() => {
    if (!openSecondDialog) return;
    setRevokeCountdown(5);
    const interval = setInterval(() => {
      setRevokeCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [openSecondDialog]);

  useEffect(() => {
    if (!openDialogDelete) return;
    setDeleteCountdown(5);
    const interval = setInterval(() => {
      setDeleteCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [openDialogDelete]);

  const cleanFile = (file: string) => file.replace("posts/", "");

  const isFileFavorite = (fileKey: string) => {
    const cleanedFile = cleanFile(fileKey);
    return posts.some((fav: any) => {
      const fileFromUrl = fav.url?.split("file_key=")[1];
      return fileFromUrl === cleanedFile;
    });
  };

  const handleFavoritePost = async (
    targetFileName: string,
    targetDescription?: string,
    isPinning = true,
  ) => {
    const success = await dispatch(patchFavoritePost(targetFileName));

    if (success) {
      toast.success(isPinning ? "Ato fixado com sucesso" : "Ato desafixado com sucesso");
    } else {
      toast.error(isPinning ? "Erro ao fixar ato" : "Erro ao desafixar ato");
    }

    const normalizedDescription = targetDescription?.trim();
    if (normalizedDescription) {
      await dispatch(patchPostDescription(targetFileName, normalizedDescription));
    }

    dispatch(fetchFavoritePosts());
  };

  const handleCreateDialogDescription = () => {
    setOpenDialogDescription(true);
    setDescription("");
    setMessage("");
  };

  const handleSubmitDescription = () => {
    if (description.length > 0 && description.length <= 200) {
      setCaracterLimit(false);
      setOpenDialogDescription(false);
      handleFavoritePost(fileName, description);
    } else if (description.length > 200) {
      setCaracterLimit(true);
      setMessage("Descrição maior que 200 caracteres");
    } else {
      setCaracterLimit(true);
      setMessage("Adicione uma descrição");
    }
  };

  const handlePinClick = (fileKey: string) => {
    const cleanedFileName = cleanFile(fileKey);
    setFileName(cleanedFileName);

    if (!isFileFavorite(fileKey)) {
      handleCreateDialogDescription();
    } else {
      handleFavoritePost(cleanedFileName, undefined, false);
    }
  };

  const handleDeletePublishedFileDialog = (row: HomeTableRow) => {
    setOpenDialogDelete(true);
    setDeleteRow(row);
  };

  const handleDeletePublishedFile = () => {
    const extractedUrl = returnHandleExtractUrl(deleteRow.presigned_url);
    setDeletingId(deleteRow.edition);
    deletePendingRef.current = true;

    dispatch(fetchDeletePublishedFile(extractedUrl ? extractedUrl : "")).finally(
      () => {
        setDeletingId(null);
        dispatch(fetchFavoritePosts());
        setOpenDialogDelete(false);
      },
    );
  };

  const redirectToHTMLViewer = (fileKey: string) => {
    const url = `${PATH.baseApi}/render-html/?file_key=${cleanFile(fileKey)}`;
    window.open(url, "_blank");
  };

  function handleRevokeEdition(fileKey: string) {
    setFileKeyToRevoke(fileKey);
    setFileKeyName(cleanFile(fileKey));
    setOpenDialog(true);
  }

  const handlePartialRevoke = () => {
    if (fileKeyToRevoke) {
      navigate(`/revogar-ato/${cleanFile(fileKeyToRevoke)}`);
    }
    setOpenDialog(false);
  };

  const handleInclude = () => {
    if (fileKeyToRevoke) {
      navigate(`/incluir/${cleanFile(fileKeyToRevoke)}`);
    }
    setOpenDialog(false);
  };

  const handleTotalRevoke = () => {
    setOpenDialog(false);
    setResponsibleRevokeFileName(undefined);
    setOpenSecondDialog(true);
  };

  const handleRevokeSecondDialogOpenChange = (open: boolean) => {
    setOpenSecondDialog(open);
    if (!open) {
      setResponsibleRevokeFileName(undefined);
    }
  };

  const filteredFiles: { file_name: string; id: string }[] = Array.isArray(
    listPosts.data,
  )
    ? listPosts.data.filter(
        (file: { file_name: string }) =>
          file.file_name.replace("/", "") !== fileKeyName,
      )
    : [];

  const currentViewMode: DataTableViewMode = isMobile ? "cards" : viewMode;

  const totalPages = Math.max(1, Math.ceil(total / 25));
  const actionButtonClassName =
    "cursor-pointer inline-flex size-8 items-center justify-center rounded-md text-muted-foreground outline-none hover:bg-accent hover:text-foreground dark:hover:bg-accent/50 dark:hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  const tableColumns = useMemo<ColumnDef<HomeTableRow, unknown>[]>(() => {
    const cols: ColumnDef<HomeTableRow, unknown>[] = [
      {
        id: "view",
        header: "Visualizar",
        cell: ({ row }: CellContext<HomeTableRow, unknown>) => (
          <div className={`flex ${currentViewMode === "table" ? "justify-center" : "justify-start"}`}>
            <GetiButton
              id="view"
              className={actionButtonClassName}
              variant="ghost"
              aria-label={`Visualizar ato ${row.original.edition}`}
              onClick={() => redirectToHTMLViewer(row.original.fileKey)}
            >
              <MdSearch />
            </GetiButton>
          </div>
        ),
      },
      ...(isLoggedIn()
        ? [
            {
              id: "pin",
              header: "Fixar",
              cell: ({ row }: CellContext<HomeTableRow, unknown>) => {
                const isPinned = isFileFavorite(row.original.fileKey);

                return (
                  <GetiButton
                    id="pinButton"
                    className={`${actionButtonClassName} ${isPinned ? styles.pinnedActionButton : ""}`}
                    variant={isPinned ? "ghost" : "primary"}
                    aria-label={`Fixar ato ${row.original.edition}`}
                    onClick={() => handlePinClick(row.original.fileKey)}
                  >
                    <MdOutlinePushPin />
                  </GetiButton>
                );
              },
            },
          ]
        : []),
      { accessorKey: "edition", header: "Ato" },
      {
        accessorKey: "origin",
        header: "Origem",
        cell: ({ getValue }: CellContext<HomeTableRow, unknown>) => (
          <span>{((getValue() as string) ?? "").replace(/-/g, "‑")}</span>
        ),
      },
      {
        accessorKey: "title",
        header: "Descrição",
        cell: ({ getValue }: CellContext<HomeTableRow, unknown>) => {
          const value = (getValue() as string) ?? "";
          const maxChars = 80; // Ajuste aqui a quantidade de caracteres
          
          const truncatedText = value.length > maxChars 
            ? value.substring(0, maxChars) + "..." 
            : value;
          
          return (
            <Tooltips placement="top" message={value}>
              <span>{truncatedText}</span>
            </Tooltips>
          );
        },
      },
      {
        accessorKey: "date",
        header: () => (
            <button
              type="button"
              className={styles.publishedSortHeader}
              onClick={() => onToggleReverse?.()}
            >
              <span className="shrink-0">Publicado</span>
              {reversePosts ? (
                <MdArrowUpward className={styles.publishedSortIcon} aria-hidden />
              ) : (
                <MdArrowDownward className={styles.publishedSortIcon} aria-hidden />
              )}
            </button>
        ),
      },
      { accessorKey: "status", header: "Status" },
      ...(isLoggedIn()
        ? [
            {
              id: "actions",
              header: () => null,
              cell: ({ row }: CellContext<HomeTableRow, unknown>) => {
                const rowEdition = row.original.edition;

                return (
                  <div onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        id="rowActions"
                        type="button"
                        className={actionButtonClassName}
                        aria-label={`Abrir menu de ações do ato ${rowEdition}`}
                      >
                        <MdMoreHoriz className="size-4" aria-hidden />
                      </DropdownMenuTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            id="revokeIcon"
                            onClick={() => handleRevokeEdition(row.original.fileKey)}
                            disabled={row.original.status === "Revogada"}
                          >
                            <MdOutlineEditNote className="size-4" aria-hidden />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            id="delete"
                            variant="destructive"
                            onClick={() => handleDeletePublishedFileDialog(row.original)}
                            disabled={deletingId === row.original.edition}
                          >
                            <MdOutlineDelete className="size-4" aria-hidden />
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenuPortal>
                    </DropdownMenu>
                  </div>
                );
              },
            },
          ]
        : []),
    ];

    return cols;
  }, [deletingId, posts, currentViewMode, reversePosts, onToggleReverse]);

  const emptyMessage = error
    ? "Não foi possível carregar as informações!"
    : "Não foi encontrado nenhum conteúdo";

  const showEmpty = isEmpty || error;
  const columnLabels: Record<string, string> = {
    pin: "Fixar",
    edition: "Ato",
    origin: "Origem",
    title: "Descrição",
    date: "Publicado",
    status: "Status",
    view: "Visualizar",
    actions: "Ações",
  };

  return (
    <div className={`space-y-3 ${styles.homeDataTableCardsSingleColumn}`}>
      {title && <div className="text-base font-semibold">{title}</div>}
      <DataTable<HomeTableRow>
        columns={tableColumns}
        data={showEmpty ? [] : data}
        emptyMessage={emptyMessage}
        columnLabels={columnLabels}
        paginationMode="server"
        serverPagination={{
          pageIndex: Math.max(page - 1, 0),
          pageCount: totalPages,
          totalRowCount: total,
          pageSize: 25,
          onPageChange: (pageIndex) => setPage(pageIndex + 1),
        }}
        isLoading={Boolean(loading && data?.length > 0)}
        isLoadingInitial={Boolean(loading && (!data || data.length === 0))}
        viewMode={currentViewMode}
        onViewModeChange={(mode) => {
          if (!isMobile) {
            setViewMode(mode);
          }
        }}
        showViewModeToggle={!isMobile}
        fixedViewMode={isMobile ? "cards" : undefined}
        columnVisibilityStorageKey={HOME_DATATABLE_COLUMN_VISIBILITY_KEY}
      />

      <GetiDialog open={openDialogDescription} onOpenChange={setOpenDialogDescription}>
        <GetiDialogContent id="pinDescription">
          <GetiDialogHeader>
            <GetiDialogTitle>Adicione a descrição</GetiDialogTitle>
            <GetiDialogDescription>Escreva uma descrição para este ato fixado, com no máximo 200 caracteres. </GetiDialogDescription>
          </GetiDialogHeader>
          <div className="px-6 pb-2">
            <textarea
              id="descriptionText"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Escreva aqui"
              maxLength={200}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[80px] resize-none"
            />
            <p className={`text-sm ${caracterLimit ? styles.errorText : "text-muted-foreground"}`}>
              {message}
            </p>
          </div>
          <GetiDialogFooter className="flex flex-row justify-end gap-2">
            <GetiButton variant="outline" onClick={() => setOpenDialogDescription(false)}>
              Cancelar
            </GetiButton>
            <GetiButton onClick={handleSubmitDescription}>Confirmar</GetiButton>
          </GetiDialogFooter>
        </GetiDialogContent>
      </GetiDialog>

      <GetiDialog open={openDialogDelete} onOpenChange={setOpenDialogDelete}>
        <GetiDialogContent>
          <GetiDialogHeader>
            <GetiDialogTitle>Você realmente deseja excluir este arquivo?</GetiDialogTitle>
            <GetiDialogDescription>
              Esta ação não pode ser desfeita.
            </GetiDialogDescription>
          </GetiDialogHeader>
          <GetiDialogFooter className="flex flex-row justify-end gap-2">
            <GetiButton variant="outline" onClick={() => setOpenDialogDelete(false)}>
              Cancelar
            </GetiButton>
            <GetiButton
              variant="destructive"
              disabled={deleteCountdown > 0}
              onClick={handleDeletePublishedFile}
            >
              {deleteCountdown > 0 ? `Confirmar (${deleteCountdown})` : "Confirmar"}
            </GetiButton>
          </GetiDialogFooter>
        </GetiDialogContent>
      </GetiDialog>

      <GetiDialog open={openDialog} onOpenChange={setOpenDialog}>
        <GetiDialogContent id="editionMenu" className={EDITION_MENU_DIALOG_CONTENT_CLASS}>
          <GetiDialogHeader className="space-y-1 px-6 pt-4 text-left">
            <GetiDialogTitle className="text-lg">O que deseja fazer?</GetiDialogTitle>
            <GetiDialogDescription>
              Selecione uma das ações abaixo para este ato. Para sair, use o botão fechar ou clique fora
              desta janela.
            </GetiDialogDescription>
          </GetiDialogHeader>
          <div className="flex flex-col gap-2 px-6 pb-6 pt-4">
            <GetiButton
              id="includeButton"
              variant="outline"
              className={EDITION_MENU_ACTION_BUTTON_CLASS}
              onClick={handleInclude}
            >
              <span className={EDITION_MENU_ICON_WRAP_CLASS}>
                <MdOutlineFileOpen className="size-5" aria-hidden />
              </span>
              <span className={EDITION_MENU_TEXT_COL_CLASS}>
                <span>Incluir</span>
                <span className={EDITION_MENU_SUBTITLE_CLASS}>Publicar ou vincular novo conteúdo</span>
              </span>
            </GetiButton>
            <GetiButton
              id="modifyButton"
              variant="outline"
              className={EDITION_MENU_ACTION_BUTTON_CLASS}
              onClick={handlePartialRevoke}
            >
              <span className={EDITION_MENU_ICON_WRAP_CLASS}>
                <MdDriveFileRenameOutline className="size-5" aria-hidden />
              </span>
              <span className={EDITION_MENU_TEXT_COL_CLASS}>
                <span>Modificar</span>
                <span className={EDITION_MENU_SUBTITLE_CLASS}>Ajustar trechos sem revogar por completo</span>
              </span>
            </GetiButton>
            <GetiButton
              id="revokeButton"
              variant="destructive"
              className={EDITION_MENU_ACTION_BUTTON_CLASS}
              onClick={handleTotalRevoke}
            >
              <span className={EDITION_MENU_REVOKE_ICON_WRAP_CLASS}>
                <MdOutlineSubtitlesOff className="size-5" aria-hidden />
              </span>
              <span className={EDITION_MENU_TEXT_COL_CLASS}>
                <span>Revogar totalmente</span>
                <span className={EDITION_MENU_REVOKE_SUBTITLE_CLASS}>Remove a vigência do ato de forma completa</span>
              </span>
            </GetiButton>
          </div>
        </GetiDialogContent>
      </GetiDialog>

      <GetiDialog open={openSecondDialog} onOpenChange={handleRevokeSecondDialogOpenChange}>
        <GetiDialogContent
          className={`${EDITION_MENU_DIALOG_CONTENT_CLASS} min-w-0`}
        >
          <GetiDialogHeader className="space-y-1 px-6 pt-4 text-left">
            <GetiDialogTitle className="text-lg">Escolha o ato responsável</GetiDialogTitle>
            <GetiDialogDescription>
              Selecione o ato que será registrado como responsável por esta revogação total.
            </GetiDialogDescription>
          </GetiDialogHeader>
          <div className="min-w-0 px-6 pb-2 pt-2">
            <Select
              value={responsibleRevokeFileName ?? REVOKE_RESPONSIBLE_NONE}
              onValueChange={(value) => {
                if (value == null || value === REVOKE_RESPONSIBLE_NONE) {
                  setResponsibleRevokeFileName(undefined);
                } else {
                  setResponsibleRevokeFileName(value);
                }
              }}
            >
              <SelectTrigger
                id="revoke-responsible-select"
                className="h-10 w-full min-w-0 max-w-full"
              >
                <SelectValue className="truncate">
                  {(value: string) =>
                    value === REVOKE_RESPONSIBLE_NONE
                      ? "Escolha o ato responsável"
                      : formatFileName(value)
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={REVOKE_RESPONSIBLE_NONE}>
                  Escolha o ato responsável
                </SelectItem>
                {filteredFiles.map((file) => (
                  <SelectItem key={file.id} value={file.file_name}>
                    {formatFileName(file.file_name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <GetiDialogFooter className="mx-0 mb-0 flex flex-row justify-end gap-2 border-t px-6 py-4">
            <GetiButton
              type="button"
              variant="outline"
              onClick={() => handleRevokeSecondDialogOpenChange(false)}
            >
              Cancelar
            </GetiButton>
            <GetiButton
              type="button"
              variant="destructive"
              disabled={!responsibleRevokeFileName || revokeCountdown > 0}
              onClick={() => {
                if (!responsibleRevokeFileName || !fileKeyName) return;
                revokePendingRef.current = true;
                dispatch(
                  updatePost({
                    file_name: fileKeyName,
                    edition: responsibleRevokeFileName,
                    status: "Revogada",
                  }),
                );
                handleRevokeSecondDialogOpenChange(false);
              }}
            >
              {revokeCountdown > 0 ? `Revogar (${revokeCountdown})` : "Revogar"}
            </GetiButton>
          </GetiDialogFooter>
        </GetiDialogContent>
      </GetiDialog>
    </div>
  );
}
